import React from "react";
import { useTranslation } from "react-i18next";
import { useUser } from "@clerk/clerk-react";
import { ArrowRight, Award, BadgeCheck, Rocket, Sparkles, Users } from "lucide-react";

// First-run welcome — a full-screen takeover shown once to a brand-new school
// admin (localStorage-gated in Dashboard), then it steps aside for the
// always-available Getting-Started checklist. Replaces the older WelcomeModal:
// same first-run moment, but full-bleed, and the copy now earns the click by
// pairing the emotional opener with the concrete steps they're about to take.
// Mirrors the CORE Getting-Started steps so the welcome promises exactly what
// the checklist delivers.
//
// ROLE-AWARE: the admin journey is about standing the school up (team, school
// profile, roster). Sub-admin staff — teachers and counselors — can do none of
// the first two: /manage-staff redirects them and school Settings are
// read-only below ADMIN. Promising those steps sent them to dead ends, so they
// get a journey built only from what their rank actually permits (inviting
// students, viewing rosters, and issuing certificates are all open to them).
const ADMIN_STEPS = [
  { key: "team", icon: Users },
  { key: "profile", icon: BadgeCheck },
  { key: "students", icon: Rocket },
];

const STAFF_STEPS = [
  { key: "staffStudents", icon: Rocket },
  { key: "staffRoster", icon: Users },
  { key: "staffCertificate", icon: Award },
];

// "Riverside High" -> "RH", "Career Services" -> "CS". Falls back to a single
// glyph so the avatars never render empty.
const initialsFrom = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

const WelcomeScreen = ({ schoolName, onStart, onSkip, isAdmin = true }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const steps = isAdmin ? ADMIN_STEPS : STAFF_STEPS;

  const adminName = user?.fullName || [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const adminInitials = initialsFrom(adminName) || user?.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase() || "";
  const schoolInitials = initialsFrom(schoolName) || "S";
  const schoolLabel = schoolName || t("welcome.schoolFallback", { defaultValue: "Your school" });

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white bg-gradient-to-b from-white via-white to-blue-50 font-['Manrope']">
      {/* Identity bar — who's setting up, for which school. */}
      <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-gray-100 bg-white/80 px-6 py-4 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary text-sm font-bold text-white">
            {schoolInitials}
          </div>
          <div className="leading-tight">
            <p className="text-[15px] font-bold text-gray-900">{schoolLabel}</p>
            <p className="text-xs text-gray-500">{t("welcome.workspace", { defaultValue: "Admin workspace" })}</p>
          </div>
        </div>
        {adminInitials ? (
          user?.imageUrl ? (
            <img src={user.imageUrl} alt={adminName || "Admin"} className="h-9 w-9 rounded-full object-cover" />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-primary">
              {adminInitials}
            </div>
          )
        ) : null}
      </div>

      {/* Hero */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto flex min-h-full max-w-[680px] flex-col items-center justify-center px-5 py-12 text-center">
          <span className="mb-6 inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3.5 py-1.5 text-sm font-semibold text-primary">
            <Sparkles size={15} />
            {t("welcome.badge", { defaultValue: "Welcome to Hypebook" })}
          </span>

          <h1 className="text-[34px] font-extrabold leading-[1.15] tracking-tight text-gray-900 sm:text-[40px]">
            {t("welcome.headline", { defaultValue: "From your classrooms to their careers." })}
          </h1>

          <p className="mt-5 max-w-[560px] text-[17px] leading-relaxed text-gray-600">
            {schoolName
              ? t("welcome.bodyNamed", {
                  defaultValue:
                    "Hypebook helps {{schoolName}} capture every student's wins, work, and proof — and carry it forward to college, career, and beyond.",
                  schoolName,
                })
              : t("welcome.body", {
                  defaultValue:
                    "Hypebook helps your school capture every student's wins, work, and proof — and carry it forward to college, career, and beyond.",
                })}
          </p>

          {/* What they're about to set up — the real onboarding steps. */}
          <p className="mt-10 mb-4 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {isAdmin
              ? t("welcome.stepsLead", { defaultValue: "What you'll set up" })
              : t("welcome.stepsLeadStaff", { defaultValue: "What you can do here" })}
          </p>
          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
            {steps.map(({ key, icon: Icon }, i) => (
              <div
                key={key}
                className="flex flex-col items-center gap-2 rounded-2xl border border-gray-100 bg-white p-4 text-center shadow-sm"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-primary">
                  <Icon size={18} />
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {i + 1}. {t(`welcome.steps.${key}.title`)}
                </span>
                <span className="text-xs leading-snug text-gray-500">{t(`welcome.steps.${key}.desc`)}</span>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={onStart}
            className="mt-10 inline-flex h-12 items-center gap-2 rounded-xl bg-primary px-7 text-[16px] font-semibold text-white shadow-sm transition-colors hover:bg-blue-600"
          >
            {isAdmin
              ? schoolName
                ? t("welcome.startCtaNamed", { defaultValue: "Set up {{schoolName}}", schoolName })
                : t("welcome.startCta", { defaultValue: "Set up your school" })
              : t("welcome.startCtaStaff", { defaultValue: "Get started" })}
            <ArrowRight size={18} />
          </button>

          <button type="button" onClick={onSkip} className="mt-3 text-sm font-medium text-gray-500 hover:text-gray-700">
            {t("welcome.skip", { defaultValue: "I'll explore on my own" })}
          </button>

          <p className="mt-7 text-xs text-gray-400">
            {isAdmin
              ? t("welcome.setupTime", { defaultValue: "Setup takes about 8 minutes · You can pause anytime" })
              : t("welcome.setupTimeStaff", { defaultValue: "Takes a couple of minutes · You can pause anytime" })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
